import React, {useEffect, useContext} from 'react';
import * as R from 'ramda';

import useAPI from '@/hooks/useAPI';
import {useProducerCtx} from '@/views/Layout/b2b/ProducerNavBar/state';
import {producerMdrt} from '@/b2b/apis';
import {mapWithIndex, toDollar} from '@/util';
import {useTranslation} from 'react-i18next';
import {SystemConfigCtx} from '@/contexts';

// const ApeInfo = ({items}) => {
//   const itemList = mapWithIndex((item, idx) => (
//     <tr key={idx}>
//       <td>{item.target_year}</td>
//       <td>${toDollar(item.ape)}</td>
//     </tr>
//   ))(items);
//
//   return (
//     <table className='table table-bordered' style={{width: '50%'}}>
//       <thead>
//         <tr>
//           <th className='table-active'>Year</th>
//           <th className='table-active'>
//             Annual Premium Equivalent (APE) Tracking
//           </th>
//         </tr>
//       </thead>
//       <tbody>{itemList}</tbody>
//     </table>
//   );
// };

// expected result
// +------+--------------------------------+------------------+----------------------+
// |      |                                | Requirment (B)   |                      |
// |      |                                +------------------+                      |
// | Year | Total Qualifying Credit (A)    | MDRT | COT | TOT | Balance (Next Target)|
// |------+--------------------------------+------------------+----------------------|
// | 2024 | Current mdrt-on-premium-yr2024 | Setting*         | = A-B (MDRT|COT|TOT) |
// | 2025 | Current mdrt-on-premium-yr2025 | Setting*         | = A-B (MDRT|COT|TOT) |
// +------+--------------------------------+------------------+----------------------+
const MdrtInfo = ({items}) => {
  const checkTarget = (current, ...targets) => {
    const sortedTargets = targets.sort((a, b) => a.value - b.value);
    const nextTarget = sortedTargets.find(target => current < target.value);

    if (nextTarget) {
      return {
        remaining: current - nextTarget.value,
        nextTarget: nextTarget.label, // Name of the target (e.g., MDRT, COT, TOT)
      };
    }

    // if all targets are exceeded, return '-' instead
    return {
      remaining: '-',
      nextTarget: null,
    };
  };

  const itemListPrem = mapWithIndex((item, idx) => {
    const {remaining, nextTarget} = checkTarget(
      item.mdrt_on_premium,
      {value: item.mdrt_target_prem, label: 'MDRT'},
      {value: item.mdrt_cot_target_prem, label: 'COT'},
      {value: item.mdrt_tot_target_prem, label: 'TOT'}
    );
    const targetResult =
      remaining === '-'
        ? '-' // If all targets are exceeded
        : `${toDollar(remaining)} (${nextTarget})`;

    return (
      <tr key={idx}>
        <td>{item.target_year}</td>
        <td>${toDollar(item.mdrt_on_premium)}</td>
        <td>${toDollar(item.mdrt_target_prem)}</td>
        <td>${toDollar(item.mdrt_cot_target_prem)}</td>
        <td>${toDollar(item.mdrt_tot_target_prem)}</td>
        <td>{targetResult}</td>
      </tr>
    );
  })(items);

  const itemListComm = mapWithIndex((item, idx) => {
    const {remaining, nextTarget} = checkTarget(
      item.mdrt_on_commission,
      {value: item.mdrt_target_comm, label: 'MDRT'},
      {value: item.mdrt_cot_target_comm, label: 'COT'},
      {value: item.mdrt_tot_target_comm, label: 'TOT'}
    );
    const targetResult =
      remaining === '-'
        ? '-' // If all targets are exceeded
        : `${toDollar(remaining)} (${nextTarget})`;

    return (
      <tr key={idx}>
        <td>{item.target_year}</td>
        <td>${toDollar(item.mdrt_on_commission)}</td>
        <td>${toDollar(item.mdrt_target_comm)}</td>
        <td>${toDollar(item.mdrt_cot_target_comm)}</td>
        <td>${toDollar(item.mdrt_tot_target_comm)}</td>
        <td>{targetResult}</td>
      </tr>
    );
  })(items);

  const MdrtTable = ({itemList, caption}) => (
    <table className='table table-bordered'>
      <thead>
        <tr>
          <th className='table-active' rowSpan='2' style={{width: '10%'}}>
            Year
          </th>
          <th className='table-active' rowSpan='2' style={{width: '20%'}}>
            Total Qualifying Credit
          </th>
          <th className='table-active' colSpan='3' style={{width: '50%'}}>
            Requirement ({caption})
          </th>
          <th className='table-active' rowSpan='2' style={{width: '20%'}}>
            Balance (Next Target)
          </th>
        </tr>
        <tr>
          <th className='table-active' style={{width: '10%'}}>
            MDRT
          </th>
          <th className='table-active' style={{width: '10%'}}>
            COT
          </th>
          <th className='table-active' style={{width: '10%'}}>
            TOT
          </th>
        </tr>
      </thead>
      <tbody>{itemList}</tbody>
    </table>
  );

  return (
    <>
      <MdrtTable itemList={itemListPrem} caption='Premium Credit' />
      <MdrtTable itemList={itemListComm} caption='Commission' />
    </>
  );
};

const Mdrt = () => {
  const [producer] = useProducerCtx();
  const {result, trigger} = useAPI(producerMdrt, {pending: true});
  const items = R.pathOr([], ['data'], result);
  const {t: tr} = useTranslation('mdrt');
  const {siteName} = useContext(SystemConfigCtx);
  const mdrtLastUpdated =
    items && items.length > 0 && items[0].mdrt_last_updated_on
      ? items[0].mdrt_last_updated_on
      : '';

  useEffect(
    () => {
      trigger(producer.producerId);
    },
    [producer]
  );

  return (
    <>
      <h3>
        {tr('title')} - {producer.producerName} {mdrtLastUpdated}
      </h3>
      <p>{tr('subtitle', {siteName})}</p>
      <MdrtInfo items={items} />
      {/* Temporary hide this section until user clarify the discrepancy between mdrt/ape */}
      {/* <ApeInfo items={items} /> */}
    </>
  );
};

export default Mdrt;
